import { defineStore, storeToRefs } from 'pinia'
import { type Raw, computed, shallowRef } from 'vue'
import { useRoute } from 'vue-router'

import { useConfig } from '@/composables/config'
import { useI18nAddon } from '@/i18n/i18n'
import { AdService, type AdServiceOptions } from '@/modules/adv/ad-service'
import { DEFAULT_HEADER_BIDDING_CONFIG } from '@/modules/adv/header-bidding/config'
import {
    ROUTES_ACCEPTING_NO_ADS_PARAM,
    ROUTES_WITH_FAST_AD,
    ROUTES_WITH_INSTREAM_ADS_AS_DEFAULT,
} from '@/router/constants'
import { useLoggerStore } from '@/store/logger'
import { AdProviderNames } from '@/types'
import { getConfigByClid } from '@/utils/clid-helpers'
import { idleCallbackPromise, timer } from '@/utils/helpers'
import { loadScript } from '@/utils/loadScript'

import { useAppStore } from './app-store'
import {
    AD_STORE_DEFAULT_PROVIDERS_FOR_GAME_PAGE_ON_PORTAL,
    AD_STORE_DEFAULT_PROVIDERS_FOR_GAME_PAGE_OUT_OF_PORTAL,
} from './constants'

const DISABLED_ADS = 'use_no_ads'
const MEMBRANA_CLID = 'p_2e32b702-bbd0-48d6-99b0-459780166adf'

export const useAdsStore = defineStore('ads', () => {
    const { VUE_APP_AD_REQUEST_TIMEOUT_MS, VUE_APP_AD_MANAGER_ACCOUNT } = useConfig()
    const { logEvent, startRumEvent } = useLoggerStore()
    const {
        clid,
        widgetId,
        userAcquisitionParams,
        isGameDistribution,
        query,
        platform,
        isIframe,
        isExportOnWidget,
        isExportOnPortal,
        experiments,
    } = storeToRefs(useAppStore())
    const adService = shallowRef<Raw<AdService> | null>(null)
    const commonOptions = computed(() => ({
        adRequestTimeoutMs: VUE_APP_AD_REQUEST_TIMEOUT_MS,
        gamAccount: VUE_APP_AD_MANAGER_ACCOUNT,
        logEvent,
        startRumEvent,
    }))
    const i18nAddon = useI18nAddon()
    const currentRoute = useRoute()
    const adsDisabled = computed(
        () => !!query.value?.[DISABLED_ADS] && ROUTES_ACCEPTING_NO_ADS_PARAM.includes(currentRoute.name as string),
    )

    const providersConfigByClid = computed(() => getConfigByClid(clid.value))

    const providerParams = computed(() => {
        if (isGameDistribution.value) {
            return { providers: [AdProviderNames.GAME_DISTRIBUTION] }
        }

        if (adsDisabled.value) {
            return { providers: [AdProviderNames.EMPTY_PROVIDER] }
        }

        const membranaAds = []
        if (clid.value === MEMBRANA_CLID) {
            // Here comes a tricky part
            // We need to make half of the requests to membrana, so it's fair to roll a coin here
            if (Math.round(Math.random())) {
                console.info('Adding Membrana ads')
                membranaAds.push(AdProviderNames.MEMBRANA_VAST, AdProviderNames.MEMBRANA)
            }
        }

        if (ROUTES_WITH_INSTREAM_ADS_AS_DEFAULT.includes(currentRoute.name as string)) {
            if (providersConfigByClid.value.inGameProviderKeys) {
                return { providers: providersConfigByClid.value.inGameProviderKeys }
            }

            // AdSense doesn't work outside the portal (playgama.com domain)
            if (!isExportOnPortal.value) {
                return { providers: [...membranaAds, ...AD_STORE_DEFAULT_PROVIDERS_FOR_GAME_PAGE_OUT_OF_PORTAL] }
            }

            return { providers: [...membranaAds, ...AD_STORE_DEFAULT_PROVIDERS_FOR_GAME_PAGE_ON_PORTAL] }
        }

        return { providers: [...membranaAds, AdProviderNames.GOOGLE_AD /*, AdProviderNames.DIRECT */] }
    })

    const commonTargeting = computed(() => ({
        playgama_clid: clid.value,
        widget_id: widgetId.value || '',
        ...userAcquisitionParams.value,
    }))

    function setAdServiceConfig(options: { targeting?: AdServiceOptions['targeting']; auid: string }) {
        const targetingFromSource = options.targeting || {}
        const targeting: AdServiceOptions['targeting'] = { ...commonTargeting.value, ...targetingFromSource }

        const isPortalEnv = !isIframe.value || isExportOnPortal.value

        const desktopFSExp = isPortalEnv && experiments.value?.['desktop-fullscreen']?.sample

        if (desktopFSExp) {
            targeting.exp = desktopFSExp
        }

        let initialDelay: Promise<void>
        if (window.additionalServerData?.isGooglebot) {
            // delay AdService init indefinitely for googlebot visits
            initialDelay = new Promise(() => {})
        } else {
            // We need to load this script early because we use it to obtain user consent.
            const GAM_SOURCE = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'
            loadScript({ src: GAM_SOURCE, useExisting: true, crossorigin: 'anonymous' })

            initialDelay =
                typeof targeting.page === 'string' && ROUTES_WITH_FAST_AD.includes(targeting.page)
                    ? idleCallbackPromise()
                    : timer(2000).then(() => idleCallbackPromise())
        }

        adService.value = new AdService({
            ...commonOptions.value,
            initialDelay,
            targeting,
            providerKeys: providerParams.value.providers,
            platform: platform.value,
            auid: options.auid,
            isWidget: isExportOnWidget.value,
            footerHeight: isExportOnPortal.value && platform.value === 'desktop' ? '0px' : '56px',
            locale: i18nAddon.currentLocale.value,
            adUnitProviderConfig: providersConfigByClid.value.inGameAdUnitProviderConfig,
            headerBiddingConfig:
                isPortalEnv && query.value?.['with_prebid'] ? DEFAULT_HEADER_BIDDING_CONFIG : undefined,
        })
    }

    function updateTargeting(targeting: AdServiceOptions['targeting']) {
        if (adService.value) {
            adService.value.updateTargeting(targeting)
        }
    }

    return {
        adService,
        setAdServiceConfig,
        updateTargeting,
    }
})
